import React, { useState, useMemo, useCallback, memo, useRef, useLayoutEffect, useEffect, useContext } from 'react';
import { List } from 'react-window';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid,
  Divider,
  Card,
  CardContent,
  useTheme,
  TableSortLabel,
  Skeleton,
  Autocomplete,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Toolbar,
  Tooltip

} from '@mui/material';

import {
  Edit as EditIcon,
  Warning as WarningIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Place as PlaceIcon,
  LocalOffer as CategoryIcon,
  Event as EventIcon,
  PictureAsPdf as PdfIcon,
  ConstructionOutlined,
  Delete as DeleteIcon,
  AccountBalanceWallet as SourceIcon,
  CompareArrows as ReviewMatchIcon,
  NorthEast as TransferOutIcon,
  SouthWest as TransferInIcon
} from '@mui/icons-material';
import { useGetEnrichedTransactionsByIDList, useGetEnrichedTransactions, useUpdateCategoryForTransactions, useUpdateMerchantForTransactions, useUpdateFlagForTransactions, useUpdateNotesForTransactions, useUpdateDescriptionForTransaction } from '../../api/transactions.js';
import { useTransferReviewActions } from '../../api/transfer_review.js';
import { deriveCardholderName, deriveCardEnding } from '../../utils/cardholder.js';
import CardholderCell from './CardholderCell.js';
import { useConfirmAction } from './confirmactionhandler.js';
import SnackbarContext from '../../context/snackbar-context.js';
import { Badge } from '../../design-system';
import { SortLevelsEditor, SortLevelsSummary } from './SortLevelsPanel.js';
import {
  addSortLevel,
  availableSortFields,
  DEFAULT_SORT_KEYS,
  describeSortKeys,
  isBlankSortKey,
  isDefaultSortKeys,
  nextSortKeys,
  SORT_FIELD_LABELS,
  SORTABLE_FIELDS,
  sortKeysSignature,
} from './transactionSortKeys.js';

// import { MerchantLocationMap, TripRouteMap } from './LocationMapComponents.js';
import TransactionDetailDialog from './TransactionDetailDialog.js';
import DataFilter from './filter/filtercontext.js';
import DateRangeFilter from './filter/filterdaterange.js';
import MultiSelectFilter from './filter/filtermultiselect.js';
import TextSearchFilter from './filter/filtertextsearch.js';
import NumericRangeFilter from './filter/filternumericrange.js';
import { formatDate, formatCurrency, formatInteger, formatPercentage, formatEntityName } from '../../utils/formatters.js'
import { perfLog, perfNow } from '../../utils/perfLog.js';
import { originalFilenameFromKey } from '../../utils/documentName.js'
import CategoryEditDialog from './CategoryEditDialog.js';
import MerchantEditDialog from './MerchantEditDialog.js';
import FlagEditDialog from './FlagEditDialog.js';
import SourceEditDialog from './SourceEditDialog.js';
import NotesEditDialog from './NotesEditDialog.js';
import DescriptionEditDialog from './DescriptionEditDialog.js';
import DescriptionCell from './DescriptionCell.js';
import TransferMatchDialog from './TransferMatchDialog.js';
// import { is } from 'date-fns/locale';
// import { set } from 'date-fns';
// import { use } from 'cytoscape';
import UserProfileContext from '../../context/userprofilecontext.js';


// Mapping function for transaction list items
const mapTransactionToListItem = (transaction, mapTransferPartner = true) => {
  // console.log("Mapping transaction to list item, mapTransferPartner:", mapTransferPartner, ", input:");
  // console.log(JSON.stringify(transaction, null, 2));

  const result = {
    bankAccountName: transaction.bankAccount?._displayName || 'Unknown Account',
    transactionDate: transaction.transaction?.posting_date || 'Unknown Date',
    description: transaction.transaction?.description || 'No Description',
    // User correction of the scanned description (set only while edited).
    descriptionEdited: transaction.transaction?.description_edited != null,
    descriptionOriginal: transaction.transaction?.description_original ?? null,
    descriptionEditedBy: transaction.transaction?.description_editedBy || null,
    descriptionEditedAt: transaction.transaction?.description_editedAt || null,
    amount: transaction.transaction?.amount || 0,
    merchantName: transaction.merchant?.name,
    merchantLocation: transaction.merchantLocation || 'Unknown Location',
    associatedNames: transaction.associatedNames?.map(name => name.name) || [],
    tripLocations: transaction.tripLocations?.map(location => location.name) || [],
    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
    categoryName: transaction.category?.name || 'Uncategorized',
    categoryType: transaction.category?.type || 'Expense',
    flagName: transaction.flag?.name,
    notes: transaction.transaction?.notes || '',
    notesUpdatedBy: transaction.transaction?.notesUpdatedBy || null,
    // Whose card (server card_view); see CardholderCell.
    cardholderName: deriveCardholderName(transaction.card_view),
    cardEnding: deriveCardEnding(transaction.card_view),
    isCardRow: transaction.card_view?.scope === 'CARD',
    // Human-verdict state of the transfer link (surfaced on transfer_partner by
    // the enriched read). Drives the disposition badge + confirm/reject controls.
    transferDisposition: transaction.transfer_partner?.disposition || null,
    transferManual: transaction.transfer_partner?.manual || false,
    // Whether this is a transfer-categorized transaction — so an UNPAIRED one
    // still gets a "Review" entry point (to match it, or restore a rejected pair).
    isTransferCategorized:
      transaction.subcategory?.type === 'Transfer' ||
      ['Transfer In', 'Transfer Out'].includes(transaction.category?.name),
  };
  if (mapTransferPartner && transaction.hasTransferPartner) {
    result.transferPartner = mapTransactionToListItem(transaction.transfer_partner, false);
  }
  // console.log("Mapped list item result:");
  // console.log(JSON.stringify(result, null, 2));
  return result;
};

// Pick the first geocode that actually carries a Google place_id. The :Merchant
// node can hold a `failed_v1` sentinel (truthy but no place_id — e.g. a check
// payee like "A-Derma Inc" with no city, which won't geocode); in that case fall
// back to the :Location (MERCHANT_LOCATION) geocode, which for a check carries the
// precise payee address resolved by enrichment. Returns null when none are usable.
const pickGeocodeWithPlaceId = (...geocodes) => {
  for (const g of geocodes) {
    if (!g) continue;
    try {
      const parsed = typeof g === 'string' ? JSON.parse(g) : g;
      if (parsed && parsed.place_id) return g;
    } catch (e) {
      // malformed geocode JSON — skip
    }
  }
  return null;
};

// Mapping function for transaction details dialog
const mapTransactionToDetailItem = (transaction) => {

  const objectKey = transaction.transaction?._awsUploadedDocumentKey || transaction.transaction?._awsObjectKey || null;

  const detailItem = {
    // Identity (used to fetch the check-image crop, etc.)
    id: transaction.transaction?.id || null,
    // Basic Information
    bankAccountName: transaction.bankAccount?._displayName || 'Unknown Account',
    accountHolder: transaction.bankAccount?.account_holder || 'Unknown Holder',
    bankName: transaction.bankAccount?.bank_name || 'Unknown Bank',
    accountType: transaction.bankAccount?.account_type || 'Unknown Type',

    // Transaction Details
    transactionDate: transaction.transaction?.posting_date || 'Unknown Date',
    description: transaction.transaction?.description || 'No Description',
    descriptionEdited: transaction.transaction?.description_edited != null,
    descriptionOriginal: transaction.transaction?.description_original ?? null,
    descriptionEditedBy: transaction.transaction?.description_editedBy || null,
    descriptionEditedAt: transaction.transaction?.description_editedAt || null,
    amount: transaction.transaction?.amount || 0,
    bucketName: transaction.transaction?._awsBucketName || null,
    objectKey: objectKey,  //DEPRECATED: _awsObjectKey is being replaced by _awsUploadedDocumentKey, but we will check for both for now to maintain compatibility with existing transactions that may not have _awsUploadedDocumentKey populated yet
    // Original upload filename recovered from the `uploads/{uuid}_{name}` key —
    // drives the PDF-preview dialog title and the download-prompt filename.
    documentName: originalFilenameFromKey(objectKey),
    bbox: transaction.transaction?.bbox || null,
    page_number: transaction.transaction?.page_number || null,

    // Merchant Information
    merchantID: transaction.merchant?.id || null,
    merchantName: transaction.merchant?.name,
    merchantLocation: transaction.merchantLocation || 'Unknown Location',
    merchantWebsite: transaction.merchant?.website || null,
    merchantPhone: transaction.merchant?.phone || null,
    merchantStreetAddress: transaction.merchant?.street_address || null,
    merchantCity: transaction.merchant?.city || null,
    merchantState: transaction.merchant?.state || null,
    merchantZip: transaction.merchant?.zip || null,
    merchantGeocode: pickGeocodeWithPlaceId(transaction.merchant?.geocode, transaction.merchantLocationGeocode),

    // Check Details (populated when this transaction was read off a check image)
    checkNumber: transaction.transaction?.check_number || null,
    checkPayee: transaction.transaction?.check_payee || null,
    checkPayeeAddress: transaction.transaction?.check_payee_address || null,
    checkMemo: transaction.transaction?.check_memo || null,
    checkImagePage: transaction.transaction?.check_image_page || null,
    // doc_kind ("issued_check" | "deposit_slip") drives the card title + labels;
    // a stamped check_image_page means a native-resolution crop exists to view.
    docKind: transaction.transaction?.doc_kind || null,
    hasCheckImage: transaction.transaction?.check_image_page != null,

    // Travel Information
    tripOrigin: transaction.tripOrigin?.name || null,
    tripOriginGeocode: transaction.tripOrigin?.geocode || null,
    tripDestination: transaction.tripDestination?.name || null,
    tripDestinationGeocode: transaction.tripDestination?.geocode || null,
    tripLocations: transaction.tripLocations || [],
    tripStartDate: transaction.transaction?._trip_start || null,
    tripEndDate: transaction.transaction?._trip_end || null,

    // Associated People
    associatedNames: transaction.associatedNames || [],

    // Categorization
    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
    categoryName: transaction.category?.name || 'Uncategorized',
    categoryRationale: transaction.category_rationale?.matched_basis || 'No rationale provided',

    // Flagging
    flagName: transaction.flag?.name || 'No Flag',
    flagRationale: transaction.flag_rationale?.matched_basis || 'No rationale provided',
    confidenceLevel: transaction.flag_rationale?.confidence_level || 'Unknown',

    // AI Analysis
    aiOutput: transaction.aiOutput || null,

    // Helper methods to check if location data is available
    hasMerchantMapData: function () {
      return !!this.merchantGeocode;
    },

    hasTripMapData: function () {
      return !!(this.tripOriginGeocode || this.tripDestinationGeocode);
    },

    hasMerchantInfo: function () {
      return (
        (this.merchantName) ||
        (this.merchantLocation && this.merchantLocation !== 'Unknown Location') ||
        this.merchantWebsite ||
        this.merchantPhone ||
        this.merchantStreetAddress ||
        this.merchantCity ||
        this.merchantState ||
        this.merchantZip
      );
    },

    hasCheckInfo: function () {
      return !!(
        this.checkNumber ||
        this.checkPayee ||
        this.checkPayeeAddress ||
        this.checkMemo
      );
    },

    // Function to determine if we have trip dates to display
    hasTripDates: function () {
      return this.tripStartDate || this.tripEndDate;
    },

    // Function to get appropriate label for trip dates section
    getTripDateLabel: function () {
      if (this.tripStartDate && this.tripEndDate) {
        return 'Travel Period';
      }
      return 'Date of Travel';
    },
  };

  return detailItem;
};

// Sorting utility functions
//
// Takes an ALREADY-MAPPED list item (see mapTransactionToListItem), not a raw
// transaction. Mapping is hoisted out to the caller so a sort maps each row once
// instead of once per operand per comparison — see sortTransactions below.
//
// Returns a genuinely EMPTY key for a missing value rather than a substitute
// that happens to sort where we want. A substitute can only be right in one
// direction: the old `'zzz'` flag sentinel pushed unflagged rows last
// ascending and pulled them first descending, which is not what a spreadsheet
// does and not what an accountant expects.
export const getComparableValue = (item, field) => {
  switch (field) {
    case 'transactionDate': {
      if (!item.transactionDate || item.transactionDate === 'Unknown Date') return null;
      const [year, month, day] = item.transactionDate.split('-').map(num => parseInt(num, 10));
      const time = new Date(year, month - 1, day).getTime();
      // An unparseable date is a blank, not the epoch.
      return Number.isNaN(time) ? null : time;
    }

    case 'amount':
      // 0 is a legitimate amount, never a blank.
      return item.amount || 0;

    // These three are coalesced upstream to 'Unknown Account' / 'No Description'
    // / 'Uncategorized', so they arrive as real strings the user can see and
    // sort — deliberately not treated as blanks.
    case 'bankAccountName':
      return (item.bankAccountName || '').toLowerCase();

    case 'description':
      return (item.description || '').toLowerCase();

    case 'subcategoryName':
      return (item.subcategoryName || '').toLowerCase();

    case 'cardholderName':
      return (item.cardholderName || '').toLowerCase();

    case 'merchantName':
      return (item.merchantName || '').toLowerCase();

    case 'flagName':
      return (item.flagName || '').toLowerCase();

    default:
      return '';
  }
};

// Sorts by the stack in order, each key carrying its own direction. An empty
// stack means "source order" and returns the input untouched.
//
// Keys are extracted once per row up front rather than inside the comparator:
// getComparableValue needs a mapped list item, and mapping is not cheap (it
// rebuilds a dozen fields). Comparator-time mapping cost ~2·n·log n mappings —
// at a few thousand rows that dominated the sort, and would have tripled again
// with three keys. Extracting first makes it n, so layering costs less than the
// old single-key sort did.
const sortTransactions = (transactions, sortKeys) => {
  if (!sortKeys?.length) return transactions;

  // mapTransferPartner=false: a paired transfer renders inside its anchor's row
  // and never sorts on its own, so the partner recursion is pure waste here.
  const decorated = transactions.map((transaction, index) => {
    const item = mapTransactionToListItem(transaction, false);
    return {
      transaction,
      index,
      keys: sortKeys.map((k) => getComparableValue(item, k.field)),
    };
  });

  decorated.sort((a, b) => {
    for (let depth = 0; depth < sortKeys.length; depth += 1) {
      const aValue = a.keys[depth];
      const bValue = b.keys[depth];

      // Blanks sort LAST in both directions, the way Excel does. That is why
      // the result is returned WITHOUT the direction flip below: negating it
      // for a descending sort would pull the blanks to the top, which is the
      // bug the old 'zzz' flag sentinel had.
      const aBlank = isBlankSortKey(aValue);
      const bBlank = isBlankSortKey(bValue);
      if (aBlank !== bBlank) return aBlank ? 1 : -1;
      // Both blank on this key — it can't order them, so fall to the next.
      if (aBlank) continue;

      let comparison = 0;

      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      if (comparison !== 0) {
        return sortKeys[depth].direction === 'desc' ? -comparison : comparison;
      }
    }

    // Fully tied on every key — fall back to source order so the sort is stable
    // regardless of engine behavior.
    return a.index - b.index;
  });

  return decorated.map((d) => d.transaction);
};

export { sortTransactions };

const MERCHANT_FIELD_TO_PAYLOAD_KEY = {
  merchantName: 'merchant_name',
  merchantWebsite: 'merchant_website',
  merchantPhone: 'merchant_phone',
  merchantStreetAddress: 'merchant_street_address',
  merchantCity: 'merchant_city',
  merchantState: 'merchant_state',
  merchantZip: 'merchant_zip'
};

// Build the PATCH /transactions/merchant payload, including only the fields the
// edit dialog actually sent. An omitted key means "leave the stored value
// alone": the :Merchant node is shared by every transaction pointing at it, so
// writing an untouched blank would wipe that value case-wide.
// A removal sends merchant_name: '' and nothing else. The server reads the
// empty name as "unlink" and only DELETEs the :MERCHANT / :MERCHANT_LOCATION
// relationships; the shared :Merchant node keeps every property it had, which
// is why no other key may ride along on a removal payload.
export const buildMerchantPayload = (merchant) => {
  if (merchant?.isRemoval) {
    return { merchant_name: '' };
  }

  return Object.entries(MERCHANT_FIELD_TO_PAYLOAD_KEY).reduce((payload, [field, key]) => {
    if (merchant?.[field] !== undefined) {
      payload[key] = merchant[field];
    }
    return payload;
  }, {});
};

// Bulk Edit Confirmation Dialog Component
const BulkEditConfirmDialog = ({ open, onClose, onConfirm, editType, editData, selectedTransactions }) => {
  const theme = useTheme();

  const getChangeDescription = () => {
    switch (editType) {
      case 'category':
        return `Change category to: ${editData.type} > ${editData.category} > ${editData.subcategory}`;
      case 'merchant':
        return editData.isRemoval
          ? `Remove the merchant assignment`
          : `Update merchant information`;
      case 'flag':
        // Check multiple possible property names for the flag
        const flagName = editData.flagName || editData.flag || editData.name;
        return flagName
          ? `Set flag to: ${flagName}`
          : 'Update flag';
      default:
        return 'Update transactions';
    }
  };

  const getMerchantChanges = () => {
    if (editData.isRemoval) {
      return ['Unlinks the merchant and its location from each selected transaction. The merchant itself is not deleted and stays available for other transactions.'];
    }

    const changes = [];
    if (editData.merchantName) changes.push(`Name: ${editData.merchantName}`);
    if (editData.merchantWebsite) changes.push(`Website: ${editData.merchantWebsite}`);
    if (editData.merchantPhone) changes.push(`Phone: ${editData.merchantPhone}`);
    if (editData.merchantStreetAddress) changes.push(`Address: ${editData.merchantStreetAddress}`);
    if (editData.merchantCity) changes.push(`City: ${editData.merchantCity}`);
    if (editData.merchantState) changes.push(`State: ${editData.merchantState}`);
    if (editData.merchantZip) changes.push(`ZIP: ${editData.merchantZip}`);
    return changes;
  };

  const getFlagChanges = () => {
    const changes = [];
    const flagName = editData.flagName || editData.flag || editData.name;
    if (flagName) changes.push(`Flag: ${flagName}`);
    if (editData.rationale) changes.push(`Rationale: ${editData.rationale}`);
    return changes;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Bulk Edit</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Change Summary
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main' }}>
            <Typography variant="body1" fontWeight="bold">
              {getChangeDescription()}
            </Typography>
            {editType === 'category' && editData.rationale && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Rationale: {editData.rationale}
              </Typography>
            )}
            {editType === 'merchant' && (
              <Box sx={{ mt: 1 }}>
                {getMerchantChanges().map((change, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    • {change}
                  </Typography>
                ))}
              </Box>
            )}
            {editType === 'flag' && (
              <Box sx={{ mt: 1 }}>
                {getFlagChanges().map((change, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    • {change}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>
            Affected Transactions ({selectedTransactions.length})
          </Typography>
          <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
            {selectedTransactions.map((transaction, idx) => {
              const item = mapTransactionToListItem(transaction);
              return (
                <Box
                  key={transaction.transaction.id}
                  sx={{
                    mb: 1,
                    pb: 1,
                    borderBottom: idx < selectedTransactions.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2">
                    <strong>{formatDate(item.transactionDate)}</strong> - {item.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.bankAccountName} • {formatCurrency(item.amount)}
                  </Typography>
                </Box>
              );
            })}
          </Paper>
        </Box>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark">
            ⚠️ This action will update {selectedTransactions.length} transaction{selectedTransactions.length !== 1 ? 's' : ''}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Confirm Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TransactionViewerByID = ({ transactionIDs, displayOptions }) => {
  // Fetch enriched transactions based on provided IDs
  const { data: transactions = [], isLoading } = useGetEnrichedTransactionsByIDList(transactionIDs, transactionIDs && transactionIDs.length > 0);
  //const filterableTransactions = transactions?.map(mapTransactionToFilterableTransaction) || [];
  const [filteredTransactions, setFilteredTransactions] = useState(transactions);
  const handleFilteredDataChange = (newFilteredData) => {
    console.log("New filtered data received:", newFilteredData);
    setFilteredTransactions(newFilteredData);
  };
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%', // Fill parent container
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // Prevent outer scrolling
      }}
    >
      {/* Filter Section - Compact and Fixed */}
      <Box sx={{
        flexShrink: 0,
        overflow: 'visible',
        zIndex: 1
      }}>
        <DataFilter
          data={transactions}
          onFilteredDataChange={handleFilteredDataChange}
          title="Filter Transactions"
          defaultCollapsed={true}
        >
          <TextSearchFilter
            fields={['description']}
            label="Description"
          />

          <DateRangeFilter
            field="transactionDate"
            label="Transaction Date"
          />

          <NumericRangeFilter
            field="amount"
            label="Amount"
            unit="$"
          />
        </DataFilter>
      </Box>
      <TransactionViewerDisplay
        transactions={filteredTransactions}
        isLoading={isLoading}
        displayOptions={displayOptions}
      />
    </Box>

  );
};

export const TransactionViewer = ({ transactions, isLoading, displayOptions, mutateTransactions, onSortedTransactionsChange, onSortDescriptionChange }) => {

  // Fetch all enriched transactions
  //const { data: transactions = [], isLoading } = useGetEnrichedTransactions();
  // const filterableTransactions = transactions?.map(mapTransactionToFilterableTransaction) || [];
  //const [filteredTransactions, setFilteredTransactions] = useState(transactions);

  // const handleFilteredDataChange = (newFilteredData) => {
  //   console.log("New filtered data received:", newFilteredData);
  //   setFilteredTransactions(newFilteredData);
  // };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%', // Fill parent container
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // Prevent outer scrolling
      }}
    >
      {/* Filter Section - Compact and Fixed
      <Box sx={{
        flexShrink: 0,
        overflow: 'visible',
        zIndex: 1
      }}>
        <DataFilter
          data={transactions}
          onFilteredDataChange={handleFilteredDataChange}
          title="Filter Transactions"
          defaultCollapsed={true}
        >
          <TextSearchFilter
            fields={['description']}
            label="Description"
          />

          <DateRangeFilter
            field="transactionDate"
            label="Transaction Date"
          />

          <NumericRangeFilter
            field="amount"
            label="Amount"
            unit="$"
          />
        </DataFilter>
      </Box> */}
      <TransactionViewerDisplay
        transactions={transactions}
        isLoading={isLoading}
        displayOptions={displayOptions}
        mutateTransactions={mutateTransactions}
        onSortedTransactionsChange={onSortedTransactionsChange}
        onSortDescriptionChange={onSortDescriptionChange}
      />
    </Box>
  );
};




// Memoized individual row component.
// Inline source-of-funds tags ([:OWNED_BY] entities, from the enriched query)
// plus the edit affordance — the TransactionViewer "Source" column. A 100% tag
// shows just the entity name; a partial tag appends the percent. Phase 2 fast-follow.
const SourceCellContent = ({ source, canEdit, onEdit }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
    {(source || []).map((s) => {
      // On-screen working view — opt into the entity's abbreviation, with the
      // full legal name on hover so the short form is never lossy.
      const name = formatEntityName(s, { abbreviate: true });
      const fullName = formatEntityName(s);
      const partial = s.pct != null && s.pct < 0.9999;
      return (
        <Chip
          key={s.entity_id}
          size="small"
          variant="outlined"
          title={fullName !== name ? fullName : undefined}
          label={partial ? `${name} ${formatPercentage(s.pct)}` : name}
        />
      );
    })}
    {canEdit && (
      <IconButton
        size="small"
        aria-label="Tag source of funds"
        onClick={onEdit}
        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
      >
        <SourceIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

// Free-form user note on a transaction. Shows the note text (if any) with an
// edit affordance; when empty, contributors still see the pencil to add one.
const NotesCellContent = ({ notes, canEdit, onEdit }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
    {notes ? (
      <Typography
        variant="body2"
        title={notes}
        sx={{
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {notes}
      </Typography>
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {canEdit ? 'Add note' : ''}
      </Typography>
    )}
    {canEdit && (
      <IconButton
        size="small"
        aria-label={notes ? 'Edit note' : 'Add note'}
        onClick={onEdit}
        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

// react-window v2's `rowComponent` is a plain function component — it does
// NOT receive a React ref (verified against react-window v2.1's d.ts). The
// internal rowRef below is purely for our own ResizeObserver-based height
// measurement, attached directly to the outer <div>.
const VirtualTransactionRow = memo(({ index, style, data }) => {
  const rowRef = useRef(null);
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext?.userProfile;

  const { onRowMeasured, measurementVersion } = data;

  // Add this to your VirtualTransactionRow component
const resizeObserverRef = useRef(null);

useLayoutEffect(() => {
  if (rowRef.current && onRowMeasured) {
    // Disconnect previous observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    // Create new ResizeObserver
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        onRowMeasured(index, height);
      }
    });

    // Observe the row
    resizeObserverRef.current.observe(rowRef.current);

    // Initial measurement using scrollHeight
    const initialHeight = rowRef.current.scrollHeight;
    onRowMeasured(index, initialHeight);
  }

  // Cleanup
  return () => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
  };
}, [index, onRowMeasured, measurementVersion]);

  const {
    sortedTransactions,
    theme,
    displayOptions,
    handleClickOpen,
    handleEditSubcategory,
    handleEditMerchant,
    handleEditFlag,
    handleEditSource,
    handleEditNotes,
    handleEditDescription,
    handleDeleteFlagClicked,
    handleReviewMatches,
    mapTransactionToListItem,
    formatDate,
    formatCurrency,
    renderSubcategoryCell,
    selectedTransactionIDs,
    handleToggleSelection
  } = data;

  const transaction = sortedTransactions[index];
  const item = mapTransactionToListItem(transaction);
  const isSelected = selectedTransactionIDs.has(transaction.transaction.id);

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    handleToggleSelection(transaction.transaction.id);
  };

  // Handle transfer partner rendering
  if (item.transferPartner) {
    const partnerSelected = transaction.transfer_partner
      ? selectedTransactionIDs.has(transaction.transfer_partner.transaction.id)
      : false;

    return (
      <div style={style} ref={rowRef}>
        {/* The MUI TableRow renders an HTML <tr>, which must live inside a
            <table><tbody> for valid DOM nesting. react-window absolutely
            positions our outer <div> (so we can't make it the table itself);
            the inner table fills the div via width:100% and the existing
            sx:{display:'table',tableLayout:'fixed'} on each TableRow keeps
            column alignment with the header table above the virtualized list. */}
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            // Hovering EITHER leg lights up the whole pair. The pairing is the
            // point of this row group, so the hover affordance has to follow
            // the pair rather than the individual row — highlighting one leg
            // alone re-introduces the "which rows belong together?" doubt this
            // group styling exists to remove. A selected leg keeps its deeper
            // value so selection stays legible while the pair is lit.
            '&:hover [data-transfer-leg]': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
            '&:hover [data-transfer-leg][data-leg-selected="true"]': {
              backgroundColor: alpha(theme.palette.primary.main, 0.24),
            },
          }}
        >
          <tbody>
        {/* Main transaction row (the out-leg).

            Both legs share one accent bar + tint and the block is closed by a
            bottom rule under the partner row, so the pair reads as a single
            enclosed group. Previously the strongest edges were a 2px rule
            BETWEEN the legs and a left bar on the partner only — an open
            bracket that made the partner look grouped with the NEXT
            transaction instead of with its own counterpart. */}
        <TableRow
          onClick={() => handleClickOpen(transaction)}
          data-transfer-leg
          data-leg-selected={isSelected ? 'true' : 'false'}
          sx={{
            cursor: 'pointer',
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            backgroundColor: isSelected
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <TableCell padding="checkbox" sx={{ width: '48px' }}>
            <Checkbox
              checked={isSelected}
              onChange={handleCheckboxClick}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ width: '100px' }}>
            {formatDate(item.transactionDate)}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <TransferOutIcon
                sx={{ fontSize: 14, color: theme.palette.primary.main, mr: 0.5 }}
                aria-hidden="true"
              />
              <Typography variant="caption" color="primary">
                Internal Transfer Out
              </Typography>
            </Box>
          </TableCell>
          <TableCell sx={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.bankAccountName}</TableCell>
          {displayOptions?.showCardholder && (
            <TableCell sx={{ width: '150px' }}>
              <CardholderCell isCard={item.isCardRow} name={item.cardholderName} ending={item.cardEnding} />
            </TableCell>
          )}
          <TableCell sx={{ width: '300px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DescriptionCell
                  item={item}
                  canEdit={userProfile?.isCaseContributor()}
                  onEdit={(e) => handleEditDescription(e, transaction)} />
              </Box>
              {/* Transfer verdict: disposition badge + confirm/reject, kept together
                  in the roomy description cell so nothing overlaps the date column. */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Badge variant={item.transferManual ? 'accent' : item.transferDisposition === 'CONFIRMED' ? 'success' : 'warning'}>
                  {item.transferManual ? 'Manual' : item.transferDisposition === 'CONFIRMED' ? 'Confirmed' : 'Proposed'}
                </Badge>
                {/* Confirm / reject happen inside the review dialog — you should
                    see the full candidate set before deciding. */}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ReviewMatchIcon fontSize="small" />}
                  onClick={(e) => handleReviewMatches(e, transaction)}
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 'var(--text-xs)', textTransform: 'none' }}
                >
                  Review
                </Button>
              </Box>
            </Box>
          </TableCell>
          <TableCell sx={{
            width: '100px',
            color: item.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
            fontWeight: 'bold'
          }}>
            {formatCurrency(item.amount)}
          </TableCell>
          {displayOptions?.showMerchant && (
            <TableCell sx={{ width: '200px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.merchantName && (
                  <>
                    {item.merchantName}
                  </>
                )}
                {userProfile?.isCaseContributor() &&
                <IconButton
                  size="small"
                  onClick={(e) => handleEditMerchant(e, transaction)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                }

              </Box>
            </TableCell>
          )}
          {renderSubcategoryCell(item, transaction)}
          {displayOptions?.showFlag && (
            <TableCell sx={{ width: '150px' }}>
              {item.flagName && (
                <Chip
                  icon={<WarningIcon />}
                  label={item.flagName}
                  color="error"
                  size="small"
                />
              )}
              {userProfile?.isCaseContributor() &&
              <IconButton
                size="small"
                onClick={(e) => handleEditFlag(e, transaction)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              }
              {userProfile?.isCaseContributor() && item.flagName && (
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteFlagClicked(e, transaction)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}

            </TableCell>
          )}
          {displayOptions?.showSource && (
            <TableCell sx={{ width: '120px' }}>
              <SourceCellContent
                source={transaction.source}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditSource(e, transaction)} />
            </TableCell>
          )}
          {displayOptions?.showNotes && (
            <TableCell sx={{ width: '200px' }}>
              <NotesCellContent
                notes={transaction.transaction?.notes}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditNotes(e, transaction)} />
            </TableCell>
          )}
        </TableRow>

        {/* Transfer partner row */}
        <TableRow
          onClick={() => handleClickOpen(transaction)}
          data-transfer-leg
          data-leg-selected={partnerSelected ? 'true' : 'false'}
          sx={{
            cursor: 'pointer',
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
            backgroundColor: partnerSelected
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.primary.main, 0.04),
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            // Closes the pair. The tint and bar stop here, so the next
            // transaction below is plainly outside the group.
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <TableCell padding="checkbox" sx={{ width: '48px' }}>
            <Checkbox
              checked={partnerSelected}
              onChange={(e) => {
                e.stopPropagation();
                if (transaction.transfer_partner) {
                  handleToggleSelection(transaction.transfer_partner.transaction.id);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ width: '100px' }}>
            <Box sx={{ display: 'flex', alignItems: 'stretch', pl: 1 }}>
              {/* Dashed rule running the height of the row: the in-leg is an
                  indented sub-row of the out-leg above it, not a peer of the
                  transaction below. */}
              <Box sx={{
                borderLeft: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                mr: 1
              }} />
              <Box>
                {formatDate(item.transferPartner.transactionDate)}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TransferInIcon
                    sx={{ fontSize: 14, color: theme.palette.primary.main, mr: 0.5 }}
                    aria-hidden="true"
                  />
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                    Internal Transfer In
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TableCell>
          <TableCell sx={{
            width: '124px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic',
            color: 'text.secondary'
          }}>
            {item.transferPartner.bankAccountName}
          </TableCell>
          {displayOptions?.showCardholder && (
            <TableCell sx={{ width: '150px' }}>
              <CardholderCell
                isCard={item.transferPartner.isCardRow}
                name={item.transferPartner.cardholderName}
                ending={item.transferPartner.cardEnding}
              />
            </TableCell>
          )}
          <TableCell sx={{
            width: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontStyle: 'italic',
            color: 'text.secondary'
          }}>
            <DescriptionCell
              item={item.transferPartner}
              canEdit={userProfile?.isCaseContributor()}
              onEdit={(e) => handleEditDescription(e, transaction.transfer_partner)} />
          </TableCell>
          <TableCell sx={{
            width: '100px',
            color: item.transferPartner.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
            fontWeight: 'bold'
          }}>
            {formatCurrency(item.transferPartner.amount)}
          </TableCell>
          {displayOptions?.showMerchant && (
            <TableCell sx={{ width: '200px', fontStyle: 'italic', color: 'text.secondary' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.transferPartner.merchantName && (
                  <>
                    {item.transferPartner.merchantName}
                  </>
                )}
                {userProfile?.isCaseContributor() &&
                <IconButton
                  size="small"
                  onClick={(e) => handleEditMerchant(e, transaction, true)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
  }
              </Box>
            </TableCell>
          )}
          {renderSubcategoryCell(item.transferPartner, transaction, true)}
          {displayOptions?.showFlag && (
            <TableCell sx={{ width: '150px' }}>
              {item.transferPartner.flagName && (
                <Chip
                  icon={<WarningIcon />}
                  label={item.transferPartner.flagName}
                  color="error"
                  size="small"
                  sx={{ opacity: 0.8 }}
                />
              )}
              {userProfile?.isCaseContributor() &&
              <IconButton
                size="small"
                onClick={(e) => handleEditFlag(e, transaction, true)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
  }
              {userProfile?.isCaseContributor() && item.transferPartner.flagName && (
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteFlagClicked(e, transaction, true)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </TableCell>
          )}
          {displayOptions?.showSource && (
            <TableCell sx={{ width: '120px' }}>
              <SourceCellContent
                source={transaction.transfer_partner?.source}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditSource(e, transaction.transfer_partner)} />
            </TableCell>
          )}
          {displayOptions?.showNotes && (
            <TableCell sx={{ width: '200px' }}>
              <NotesCellContent
                notes={transaction.transfer_partner?.transaction?.notes}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditNotes(e, transaction.transfer_partner)} />
            </TableCell>
          )}
        </TableRow>
          </tbody>
        </Box>
      </div>
    );
  }

  // Single transaction row — same table/tbody wrap as the transferPartner
  // branch above, see comment there for why.
  return (
    <div style={style} ref={rowRef}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <tbody>
      <TableRow
        onClick={() => handleClickOpen(transaction)}
        hover
        sx={{
          cursor: 'pointer',
          display: 'table',
          width: '100%',
          tableLayout: 'fixed',
          '&:hover': { backgroundColor: theme.palette.action.hover },
          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'inherit',
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '48px' }}>
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxClick}
            onClick={(e) => e.stopPropagation()}
          />
        </TableCell>
        <TableCell sx={{ width: '100px' }}>
          {formatDate(item.transactionDate)}
        </TableCell>
        <TableCell sx={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.bankAccountName}</TableCell>
        {displayOptions?.showCardholder && (
          <TableCell sx={{ width: '150px' }}>
            <CardholderCell isCard={item.isCardRow} name={item.cardholderName} ending={item.cardEnding} />
          </TableCell>
        )}
        <TableCell sx={{ width: '300px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DescriptionCell
                item={item}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditDescription(e, transaction)} />
            </Box>
            {/* An unpaired transfer-categorized txn still gets a Review entry
                point — to match it, or restore a pair the reviewer unmatched. */}
            {item.isTransferCategorized && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <Badge variant="warning">Unmatched</Badge>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ReviewMatchIcon fontSize="small" />}
                  onClick={(e) => handleReviewMatches(e, transaction)}
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 'var(--text-xs)', textTransform: 'none' }}
                >
                  Review
                </Button>
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{
          width: '100px',
          color: item.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
          fontWeight: 'bold'
        }}>
          {formatCurrency(item.amount)}
        </TableCell>
        {displayOptions?.showMerchant && (
          <TableCell sx={{ width: '200px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {item.merchantName && (
                <>
                  {item.merchantName}
                </>
              )}
              {userProfile?.isCaseContributor() &&
              <IconButton
                size="small"
                onClick={(e) => handleEditMerchant(e, transaction)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
}
            </Box>
          </TableCell>
        )}
        {renderSubcategoryCell(item, transaction)}
        {displayOptions?.showFlag && (
          <TableCell sx={{ width: '150px' }}>
            {item.flagName && (
              <Chip
                icon={<WarningIcon />}
                label={item.flagName}
                color="error"
                size="small"
              />
            )}
            {userProfile?.isCaseContributor() &&
            <IconButton
              size="small"
              onClick={(e) => handleEditFlag(e, transaction)}
              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
}
            {userProfile?.isCaseContributor() && item.flagName && (
              <IconButton
                size="small"
                onClick={(e) => handleDeleteFlagClicked(e, transaction)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </TableCell>
        )}
        {displayOptions?.showSource && (
          <TableCell sx={{ width: '120px' }}>
            <SourceCellContent
              source={transaction.source}
              canEdit={userProfile?.isCaseContributor()}
              onEdit={(e) => handleEditSource(e, transaction)} />
          </TableCell>
        )}
        {displayOptions?.showNotes && (
          <TableCell sx={{ width: '200px' }}>
            <NotesCellContent
              notes={transaction.transaction?.notes}
              canEdit={userProfile?.isCaseContributor()}
              onEdit={(e) => handleEditNotes(e, transaction)} />
          </TableCell>
        )}
      </TableRow>
        </tbody>
      </table>
    </div>
  );
});

const TransactionViewerDisplay = ({ transactions, isLoading, displayOptions, mutateTransactions, onSortedTransactionsChange, onSortDescriptionChange }) => {
  // NOTE: do NOT console.log the full `transactions` array here. It runs on
  // every render and Chromium retains the logged reference indefinitely (never
  // GC'd), so on a large case the tab leaks memory monotonically until it OOMs.
  // It also dumped transaction PII (names, descriptions, amounts) to the console.
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext?.userProfile;
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  // Layered sort stack, index 0 = primary key. See nextSortKeys for the rules.
  const [sortKeys, setSortKeys] = useState(DEFAULT_SORT_KEYS);

  // Selection state
  const [selectedTransactionIDs, setSelectedTransactionIDs] = useState(new Set());

  // for dynamic row height measurement for the react-window list
  const [measuredHeights, setMeasuredHeights] = useState(new Map());
  const [measurementVersion, setMeasurementVersion] = useState(0);

  const prevTransactionsRef = useRef([]);
  const prevSortSignatureRef = useRef(sortKeysSignature(DEFAULT_SORT_KEYS));
  const prevPairingSignatureRef = useRef('');




  // Subcategory editing state
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [openSubcategoryEdit, setOpenSubcategoryEdit] = useState(false);
  const [openMerchantEdit, setOpenMerchantEdit] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [openFlagEdit, setOpenFlagEdit] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  // Account-ownership Phase 2: per-transaction source-of-funds tagging.
  const [openSourceEdit, setOpenSourceEdit] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  // Free-form user note per transaction.
  const [openNotesEdit, setOpenNotesEdit] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [editingDescription, setEditingDescription] = useState(null);
  const [showDeleteFlagConfirm, setShowDeleteFlagConfirm] = React.useState(false);

  // Bulk edit confirmation state
  const [bulkEditConfirm, setBulkEditConfirm] = useState({
    open: false,
    type: null,
    data: null
  });

  const theme = useTheme();

  const updateCategoryForTransactions = useUpdateCategoryForTransactions(mutateTransactions);
  const updateMerchantForTransactions = useUpdateMerchantForTransactions(mutateTransactions);
  const updateFlagForTransactions = useUpdateFlagForTransactions(mutateTransactions);
  const updateNotesForTransactions = useUpdateNotesForTransactions(mutateTransactions);
  const updateDescriptionForTransaction = useUpdateDescriptionForTransaction(mutateTransactions);

  // Manual transfer review (ADR-001): confirm a proposed transfer, or reject it
  // (durable NOT_TRANSFER suppression). Optimistic + revalidate via the same
  // enriched-transactions mutate the other edits use.
  const { openSnackbarError, openSnackbarSuccess } = useContext(SnackbarContext) || {};
  const transferReview = useTransferReviewActions(mutateTransactions);

  // All transfer verdicts happen inside the review dialog (after seeing the full
  // candidate set) — the grid row only opens it. Every change action is gated by
  // a confirmation dialog; the mutations are optimistic (see api/transfer_review).
  const [matchDialogAnchor, setMatchDialogAnchor] = useState(null);

  // Slim display legs for the optimistic re-match.
  const legFromAnchor = (a) => ({
    txn_id: a.transaction.id, posting_date: a.transaction.posting_date,
    description: a.transaction.description, amount: a.transaction.amount,
    account_display: a.bankAccount?._displayName, sub_account_number: a.bankAccount?.sub_account_number,
  });
  const legFromCandidate = (c) => ({
    txn_id: c.txn_id, posting_date: c.posting_date, description: c.description,
    amount: c.amount, account_display: c.account_display, sub_account_number: c.sub_account_number,
  });

  const { handleConfirmAction: confirmRejectTransfer, ConfirmDialogElement: RejectTransferDialog } = useConfirmAction(
    (txn) => {
      setMatchDialogAnchor(null);
      transferReview
        .reject(txn.transaction.id, txn.transfer_partner.transaction.id)
        .then(() => openSnackbarSuccess?.('Transfer link removed.'))
        .catch(() => openSnackbarError?.('Could not remove the transfer link.'));
    },
    {
      title: 'Remove transfer link',
      getEntityName: (txn) => txn?.transaction?.description || 'this transfer',
      confirmMessage: 'This unlinks the two transactions and stops the system from re-proposing this pair. You can restore it later from either transaction.',
      proceedButtonLabel: 'Remove link',
      destructive: true,
    }
  );

  const { handleConfirmAction: confirmConfirmTransfer, ConfirmDialogElement: ConfirmMatchDialog } = useConfirmAction(
    ({ anchorId, partnerId }) => {
      setMatchDialogAnchor(null);
      transferReview
        .confirm(anchorId, partnerId)
        .then(() => openSnackbarSuccess?.('Transfer confirmed.'))
        .catch(() => openSnackbarError?.('Could not confirm the transfer.'));
    },
    {
      title: 'Confirm transfer match',
      getEntityName: () => 'this transfer match',
      confirmMessage: 'Lock this as the confirmed match. The system will not change it automatically afterward.',
      proceedButtonLabel: 'Confirm match',
    }
  );

  const { handleConfirmAction: confirmLinkTransfer, ConfirmDialogElement: LinkMatchDialog } = useConfirmAction(
    ({ anchor, candidate }) => {
      setMatchDialogAnchor(null);
      transferReview
        .link(legFromAnchor(anchor), legFromCandidate(candidate))
        .then(() => openSnackbarSuccess?.('Transfer match updated.'))
        .catch(() => openSnackbarError?.('Could not update the transfer match.'));
    },
    {
      title: 'Change transfer match',
      getEntityName: ({ candidate } = {}) => candidate?.description || 'the selected candidate',
      confirmMessage: 'Re-point this transfer to the selected candidate. This replaces the current match and frees any prior partner.',
      proceedButtonLabel: 'Use this match',
    }
  );

  const handleReviewMatches = useCallback((event, transaction) => {
    event.stopPropagation();
    setMatchDialogAnchor(transaction);
  }, []);
  const handleSelectMatch = useCallback((candidate) => {
    if (matchDialogAnchor && candidate) confirmLinkTransfer({ anchor: matchDialogAnchor, candidate });
  }, [matchDialogAnchor, confirmLinkTransfer]);
  const handleDialogConfirm = useCallback((anchorId, partnerId) => {
    if (anchorId && partnerId) confirmConfirmTransfer({ anchorId, partnerId });
  }, [confirmConfirmTransfer]);
  const handleDialogReject = useCallback(() => {
    if (matchDialogAnchor?.transfer_partner) confirmRejectTransfer(matchDialogAnchor);
  }, [matchDialogAnchor, confirmRejectTransfer]);

  const getTransactionRowHeight = useCallback((index, rowProps) => {
    const normalHeight = 73; // Base height for a single row
    const transferHeight = normalHeight * 2 + 4; // Additional height for transfer partner row
    // console.log("Calculating row height for index:", index);
    // console.log("Data:", rowProps);

    const measuredHeight = measuredHeights.get(index);
    if (measuredHeight) {
      // console.log("Using measured height:", measuredHeight);
      return measuredHeight;
    }

    const {
      sortedTransactions,
      mapTransactionToListItem,
    } = rowProps.data;
    const transaction = sortedTransactions[index];
    const item = mapTransactionToListItem(transaction);
    return item.transferPartner ? transferHeight : normalHeight;
  }, [measuredHeights]);

  // Memoize sorted transactions
  const sortedTransactions = useMemo(() => {
    const _t0 = perfNow();
    const sorted = sortTransactions(transactions, sortKeys);
    // Diagnostic-only (gated, primitives only): sort cost + how often it recomputes.
    perfLog('view.sort', {
      rows: Array.isArray(transactions) ? transactions.length : 0,
      keys: sortKeys.length,
      ms: +(perfNow() - _t0).toFixed(1),
    });
    return sorted;
  }, [transactions, sortKeys]);

  // Surface the sort order to the parent so PDF print can render rows in
  // the same order the user is viewing. Memo above is referentially stable
  // unless transactions/sortKeys change (sortKeys only gets a new identity from
  // setSortKeys, never from a render), so this won't loop.
  useEffect(() => {
    if (typeof onSortedTransactionsChange === 'function') {
      onSortedTransactionsChange(sortedTransactions);
    }
  }, [sortedTransactions, onSortedTransactionsChange]);

  // Same idea for the sort order in words, so the printed report can state it.
  // Kept separate from the effect above because the description only depends on
  // the stack, not on the rows.
  const sortDescription = useMemo(() => describeSortKeys(sortKeys), [sortKeys]);

  useEffect(() => {
    if (typeof onSortDescriptionChange === 'function') {
      onSortDescriptionChange(sortDescription);
    }
  }, [sortDescription, onSortDescriptionChange]);

  // Get selected transactions
  const selectedTransactions = useMemo(() => {
    return sortedTransactions.filter(t => selectedTransactionIDs.has(t.transaction.id));
  }, [sortedTransactions, selectedTransactionIDs]);

  // Callback to handle row measurement updates

  const handleRowMeasured = useCallback((index, height) => {
    setMeasuredHeights((prev) => new Map(prev).set(index, height));
}, []);

// Add this useEffect to handle window resize
useEffect(() => {
  let resizeTimeout;

  const handleWindowResize = () => {
    // Clear previous timeout
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }

    // Debounce the resize handling.
    // react-window v2 has no resetAfterIndex — its ListImperativeAPI exposes
    // only `element` and `scrollToRow`. Heights are re-queried via the
    // rowHeight callback on every render, so clearing measuredHeights +
    // bumping measurementVersion (forces re-render) is sufficient to make
    // the List request fresh sizes for visible rows.
    resizeTimeout = setTimeout(() => {
      setMeasuredHeights(new Map());
      setMeasurementVersion(prev => prev + 1);
    }, 150); // Wait 150ms after resize stops
  };

  window.addEventListener('resize', handleWindowResize);

  return () => {
    window.removeEventListener('resize', handleWindowResize);
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
  };
}, []);

  // Clear measured heights when transactions change
  useEffect(() => {
    // The whole stack matters, not just the top key: promoting a tie-breaker or
    // flipping its direction reorders rows exactly like changing the primary key
    // does, and the height cache is index-keyed.
    const sortSignature = sortKeysSignature(sortKeys);
    const sortChanged = prevSortSignatureRef.current !== sortSignature;

    const countChanged =
      prevTransactionsRef.current.length !== sortedTransactions.length;

    // A row's height depends on whether it's a paired transfer (double height) or
    // single. Confirming/rejecting/re-matching a transfer flips a row between
    // single and paired WITHOUT changing the count — the index-keyed measured
    // height would otherwise stay stale and the paired row bleeds onto the next.
    // So also reset when the paired-vs-single pattern changes.
    const pairingSignature = sortedTransactions.map((t) => (t.transfer_partner ? '1' : '0')).join('');
    const pairingChanged = prevPairingSignatureRef.current !== pairingSignature;

    // Clearing the cache makes getTransactionRowHeight fall back to the correct
    // transferPartner-aware height until the ResizeObserver re-measures.
    if (sortChanged || countChanged || pairingChanged) {
      setMeasuredHeights(new Map());
      setMeasurementVersion(prev => prev + 1);
    }

    // Update refs
    prevTransactionsRef.current = sortedTransactions;
    prevSortSignatureRef.current = sortSignature;
    prevPairingSignatureRef.current = pairingSignature;
  }, [sortedTransactions, sortKeys]);

  // Merchant / Subcategory / Flag are optional columns. If one carrying a sort
  // key gets hidden, drop the key — an invisible tie-breaker silently reorders
  // both the table and the printed report with no control left to undo it.
  useEffect(() => {
    const columnVisible = {
      cardholderName: !!displayOptions?.showCardholder,
      merchantName: !!displayOptions?.showMerchant,
      subcategoryName: !!displayOptions?.showSubcategory,
      flagName: !!displayOptions?.showFlag,
    };

    setSortKeys((prev) => {
      const pruned = prev.filter((k) => columnVisible[k.field] !== false);
      // Same reference when nothing was pruned: displayOptions is often an inline
      // object, so this effect re-runs constantly and must not churn identity.
      if (pruned.length === prev.length) return prev;
      // Pruning everything would leave the table in raw load order, which is
      // arbitrary — bad for a view that feeds court exhibits. Fall back to the
      // defined default instead. (Emptying the stack from the panel IS allowed:
      // that is a deliberate act, and this effect never sees it.)
      return pruned.length ? pruned : DEFAULT_SORT_KEYS;
    });
  }, [displayOptions]);

  // Selection handlers
  const handleToggleSelection = useCallback((id) => {
    setSelectedTransactionIDs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTransactionIDs.size === sortedTransactions.length) {
      setSelectedTransactionIDs(new Set());
    } else {
      setSelectedTransactionIDs(new Set(sortedTransactions.map(t => t.transaction.id)));
    }
  }, [sortedTransactions, selectedTransactionIDs.size]);

  const handleClearSelection = useCallback(() => {
    setSelectedTransactionIDs(new Set());
  }, []);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleSort = useCallback((field) => {
    setSortKeys((prev) => nextSortKeys(prev, field));
  }, []);

  const handleResetSort = useCallback(() => {
    setSortKeys(DEFAULT_SORT_KEYS);
  }, []);

  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const handleToggleSortPanel = useCallback(() => setSortPanelOpen((prev) => !prev), []);

  // Only the columns actually on screen can be sort levels — a level on a
  // hidden column would reorder the table and the export with no visible cause.
  // Depends on the three BOOLEANS, not on displayOptions itself — callers pass
  // an inline object, so an object dep would hand the panel a fresh array on
  // every render.
  const showCardholder = !!displayOptions?.showCardholder;
  const showMerchant = !!displayOptions?.showMerchant;
  const showSubcategory = !!displayOptions?.showSubcategory;
  const showFlag = !!displayOptions?.showFlag;
  const sortableVisibleFields = useMemo(
    () => SORTABLE_FIELDS.filter((field) => {
      if (field === 'cardholderName') return showCardholder;
      if (field === 'merchantName') return showMerchant;
      if (field === 'subcategoryName') return showSubcategory;
      if (field === 'flagName') return showFlag;
      return true;
    }),
    [showCardholder, showMerchant, showSubcategory, showFlag],
  );

  // Props shared by every sortable column header. A header arrow is a
  // single-column control, so there is no precedence to announce — the stack is
  // stated in the sort-levels panel instead.
  const sortLabelProps = useCallback((field, columnLabel) => {
    const entry = sortKeys.find((k) => k.field === field);
    const direction = entry ? entry.direction : 'asc';
    const directionLabel = direction === 'asc' ? 'ascending' : 'descending';

    return {
      active: !!entry,
      direction,
      onClick: () => handleSort(field),
      'aria-label': entry
        ? `${columnLabel}, sorted ${directionLabel}. Activate to reverse, or use Sort Columns to sort by more than one.`
        : `Sort by ${columnLabel}`,
    };
  }, [sortKeys, handleSort]);

  // aria-sort belongs on exactly one column, so only the primary key gets it.
  const ariaSortFor = useCallback((field) => {
    if (sortKeys[0]?.field !== field) return undefined;
    return sortKeys[0].direction === 'asc' ? 'ascending' : 'descending';
  }, [sortKeys]);

  const handleClickOpen = useCallback((transaction) => {
    setSelectedTransaction(mapTransactionToDetailItem(transaction));
    setOpenDetail(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenDetail(false);
  }, []);

  // Modified edit handlers for bulk editing
  const handleEditSubcategory = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    // If we have selected transactions, use bulk edit mode
    if (selectedTransactionIDs.size > 0) {
      // Bulk edit mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk edit");
        return;
      }

      const editingSubcategory = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        currentCategoryType: relevant_transaction.category?.type,
        currentCategory: relevant_transaction.category?.name,
        currentSubcategory: relevant_transaction.subcategory?.name,
        currentRationale: relevant_transaction.category_rationale?.matched_basis,
        // Ledger segment (categorization-segments ADR-001): the current
        // subcategory's chart, and the account's default chart.
        currentSegment: relevant_transaction.subcategory?.segment,
        accountSegment: relevant_transaction.bankAccount?.ledger_segment
      };

      setEditingSubcategory(editingSubcategory);
      setOpenSubcategoryEdit(true);
    } else {
      // Single transaction edit mode
      if (!transaction) {
        console.error("No transaction provided for single edit");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single edit");
        return;
      }

      const editingSubcategory = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        currentCategoryType: relevant_transaction.category?.type,
        currentCategory: relevant_transaction.category?.name,
        currentSubcategory: relevant_transaction.subcategory?.name,
        currentRationale: relevant_transaction.category_rationale?.matched_basis,
        // Ledger segment (categorization-segments ADR-001): the current
        // subcategory's chart, and the account's default chart.
        currentSegment: relevant_transaction.subcategory?.segment,
        accountSegment: relevant_transaction.bankAccount?.ledger_segment
      };

      setEditingSubcategory(editingSubcategory);
      setOpenSubcategoryEdit(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  const handleEditMerchant = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    if (selectedTransactionIDs.size > 0) {
      // Bulk edit mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk edit");
        return;
      }

      const editingMerchant = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        merchantID: relevant_transaction.merchant?.id || null,
        merchantName: relevant_transaction.merchant?.name || null,
        merchantWebsite: relevant_transaction.merchant?.website || null,
        merchantPhone: relevant_transaction.merchant?.phone || null,
        merchantStreetAddress: relevant_transaction.merchant?.street_address || null,
        merchantCity: relevant_transaction.merchant?.city || null,
        merchantState: relevant_transaction.merchant?.state || null,
        merchantZip: relevant_transaction.merchant?.zip || null,
        merchantGeocode: relevant_transaction.merchant?.geocode || null,
      };

      setEditingMerchant(editingMerchant);
      setOpenMerchantEdit(true);
    } else {
      // Single transaction edit mode
      if (!transaction) {
        console.error("No transaction provided for single edit");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single edit");
        return;
      }

      const editingMerchant = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        merchantID: relevant_transaction.merchant?.id || null,
        merchantName: relevant_transaction.merchant?.name || null,
        merchantWebsite: relevant_transaction.merchant?.website || null,
        merchantPhone: relevant_transaction.merchant?.phone || null,
        merchantStreetAddress: relevant_transaction.merchant?.street_address || null,
        merchantCity: relevant_transaction.merchant?.city || null,
        merchantState: relevant_transaction.merchant?.state || null,
        merchantZip: relevant_transaction.merchant?.zip || null,
        merchantGeocode: relevant_transaction.merchant?.geocode || null,
      };

      setEditingMerchant(editingMerchant);
      setOpenMerchantEdit(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  const handleEditFlag = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    if (selectedTransactionIDs.size > 0) {
      // Bulk edit mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk edit");
        return;
      }

      const editingFlag = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setOpenFlagEdit(true);
    } else {
      // Single transaction edit mode
      if (!transaction) {
        console.error("No transaction provided for single edit");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single edit");
        return;
      }

      const editingFlag = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setOpenFlagEdit(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  const handleDeleteFlagClicked = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    if (selectedTransactionIDs.size > 0) {
      // Bulk delete mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk delete");
        return;
      }

      const editingFlag = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setShowDeleteFlagConfirm(true);
    } else {
      // Single transaction delete mode
      if (!transaction) {
        console.error("No transaction provided for single delete");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single delete");
        return;
      }

      const editingFlag = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setShowDeleteFlagConfirm(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  // Modified save handlers for bulk operations
  const handleSaveSubcategory = (newSubcategory) => {
    if (editingSubcategory?.isBulkEdit) {
      // Show confirmation dialog for bulk edit
      setBulkEditConfirm({
        open: true,
        type: 'category',
        data: newSubcategory
      });
    } else {
      // Single transaction edit
      const transactionIDList = [editingSubcategory.enrichedTransaction.transaction.id];
      const confidenceLevel = "Assigned by User";
      const predictionModel = "User Edit";
      updateCategoryForTransactions(transactionIDList, newSubcategory.type, newSubcategory.category, newSubcategory.subcategory, confidenceLevel, newSubcategory.rationale, predictionModel, newSubcategory.segment);
      handleCloseSubcategoryEdit();
    }
  };

  const handleSaveMerchant = (updatedMerchant) => {
    if (editingMerchant?.isBulkEdit) {
      setBulkEditConfirm({
        open: true,
        type: 'merchant',
        data: updatedMerchant
      });
    } else {
      console.log('Saving merchant:', {
        oldMerchant: editingMerchant,
        newMerchant: updatedMerchant
      });

      const transactionIDList = [editingMerchant.enrichedTransaction.transaction.id];
      const merchantData = buildMerchantPayload(updatedMerchant);

      updateMerchantForTransactions(transactionIDList, merchantData);
      handleCloseMerchantEdit();
    }
  };

  const handleSaveFlag = (updatedFlag) => {
    if (editingFlag?.isBulkEdit) {
      setBulkEditConfirm({
        open: true,
        type: 'flag',
        data: updatedFlag
      });
    } else {
      console.log('Saving flag:', {
        oldFlag: editingFlag,
        newFlag: updatedFlag
      });

      const transactionIDList = [editingFlag.enrichedTransaction.transaction.id];
      const flagData = {
        ...updatedFlag
      };

      updateFlagForTransactions(transactionIDList, flagData);
      handleCloseFlagEdit();
    }
  };

  const handleDeleteFlag = () => {
    if (editingFlag?.isBulkEdit) {
      const transactionIDList = Array.from(selectedTransactionIDs);
      const flagData = {};
      updateFlagForTransactions(transactionIDList, flagData);
      setShowDeleteFlagConfirm(false);
      setEditingFlag(null);
      handleClearSelection();
    } else {
      const transactionIDList = [editingFlag.enrichedTransaction.transaction.id];
      const flagData = {};
      updateFlagForTransactions(transactionIDList, flagData);
      setShowDeleteFlagConfirm(false);
      setEditingFlag(null);
    }
  };

  const handleDeleteFlagConfirm = () => {
    handleDeleteFlag();
  };

  const handleDeleteFlagCancel = () => {
    setShowDeleteFlagConfirm(false);
    setEditingFlag(null);
  };

  // Bulk edit confirmation handler
  const handleBulkEditConfirm = () => {
    const transactionIDList = Array.from(selectedTransactionIDs);

    switch (bulkEditConfirm.type) {
      case 'category':
        const confidenceLevel = "Assigned by User";
        const predictionModel = "User Edit";
        updateCategoryForTransactions(
          transactionIDList,
          bulkEditConfirm.data.type,
          bulkEditConfirm.data.category,
          bulkEditConfirm.data.subcategory,
          confidenceLevel,
          bulkEditConfirm.data.rationale,
          predictionModel,
          bulkEditConfirm.data.segment
        );
        handleCloseSubcategoryEdit();
        break;

      case 'merchant':
        const merchantData = buildMerchantPayload(bulkEditConfirm.data);
        updateMerchantForTransactions(transactionIDList, merchantData);
        handleCloseMerchantEdit();
        break;

      case 'flag':
        const flagData = { ...bulkEditConfirm.data };
        updateFlagForTransactions(transactionIDList, flagData);
        handleCloseFlagEdit();
        break;
    }

    setBulkEditConfirm({ open: false, type: null, data: null });
    handleClearSelection();
  };

  const handleCloseSubcategoryEdit = () => {
    setOpenSubcategoryEdit(false);
    setEditingSubcategory(null);
  };

  const handleCloseMerchantEdit = () => {
    setOpenMerchantEdit(false);
    setEditingMerchant(null);
  };

  const handleCloseFlagEdit = () => {
    setOpenFlagEdit(false);
    setEditingFlag(null);
  };

  // Open the source-of-funds editor for one transaction (the main row or a
  // transfer-partner row passes the relevant enriched transaction). Single-
  // transaction only — source is per-transaction, not bulk.
  const handleEditSource = useCallback((event, txnObj) => {
    event.stopPropagation();
    const t = txnObj?.transaction;
    if (!t?.id) return;
    setEditingSource({ transactionId: t.id, amount: t.amount });
    setOpenSourceEdit(true);
  }, []);

  const handleCloseSourceEdit = () => {
    setOpenSourceEdit(false);
    setEditingSource(null);
  };

  // Open the note editor for one transaction (main row or transfer-partner row).
  // Single-transaction only — a note is per-transaction, not bulk.
  const handleEditNotes = useCallback((event, txnObj) => {
    event.stopPropagation();
    const t = txnObj?.transaction;
    if (!t?.id) return;
    setEditingNotes({ transactionId: t.id, notes: t.notes || '' });
    setOpenNotesEdit(true);
  }, []);

  const handleCloseNotesEdit = () => {
    setOpenNotesEdit(false);
    setEditingNotes(null);
  };

  const handleSaveNotes = (notes) => {
    if (!editingNotes?.transactionId) return;
    updateNotesForTransactions([editingNotes.transactionId], notes);
    handleCloseNotesEdit();
  };

  // Correct an OCR-misread description (main row or transfer-partner row).
  // Single-transaction only — a description is row-specific.
  const handleEditDescription = useCallback((event, txnObj) => {
    event.stopPropagation();
    const t = txnObj?.transaction;
    if (!t?.id) return;
    setEditingDescription({
      transactionId: t.id,
      description: t.description || '',
      originalDescription: t.description_edited != null ? (t.description_original ?? '') : null,
    });
  }, []);

  const handleCloseDescriptionEdit = () => setEditingDescription(null);

  const handleSaveDescription = (description) => {
    if (!editingDescription?.transactionId) return;
    const reverting = !description;
    Promise.resolve(updateDescriptionForTransaction(editingDescription.transactionId, description))
      .then(() => openSnackbarSuccess?.(reverting
        ? 'Description reverted to the scanned text.'
        : 'Description updated. Categorization will refresh shortly.'))
      .catch(() => openSnackbarError?.('Could not update the description.'));
    handleCloseDescriptionEdit();
  };

  // Memoize subcategory cell render function
  const renderSubcategoryCell = useCallback((item, transaction, isTransferPartner = false) => {
    if (!displayOptions?.showSubcategory) return null;

    return (
      <TableCell sx={{ width: '150px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<CategoryIcon />}
            label={item.subcategoryName}
            size="small"
            variant="outlined"
            sx={{ opacity: isTransferPartner ? 0.8 : 1 }}
          />
          {userProfile?.isCaseContributor() &&
          <IconButton
            size="small"
            onClick={(e) => handleEditSubcategory(e, transaction, isTransferPartner)}
            sx={{
              opacity: 0.5,
              '&:hover': { opacity: 1 },
              transition: 'opacity 0.2s'
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
  }
        </Box>
      </TableCell>
    );
  }, [displayOptions, handleEditSubcategory, userProfile]);

  // Data to pass to virtual rows
  const itemData = useMemo(() => ({
    sortedTransactions,
    theme,
    displayOptions,
    handleClickOpen,
    handleEditSubcategory,
    handleEditMerchant,
    handleEditFlag,
    handleEditSource,
    handleEditNotes,
    handleEditDescription,
    handleDeleteFlagClicked,
    handleReviewMatches,
    mapTransactionToListItem,
    formatDate,
    formatCurrency,
    renderSubcategoryCell,
    onRowMeasured: handleRowMeasured,
    measurementVersion,
    selectedTransactionIDs,
    handleToggleSelection
  }), [
    sortedTransactions,
    theme,
    displayOptions,
    handleClickOpen,
    handleEditSubcategory,
    handleEditMerchant,
    handleEditFlag,
    handleEditSource,
    handleEditNotes,
    handleEditDescription,
    handleDeleteFlagClicked,
    handleReviewMatches,
    renderSubcategoryCell,
    handleRowMeasured,
    mapTransactionToListItem,
    formatDate,
    formatCurrency,
    measurementVersion,
    selectedTransactionIDs,
    handleToggleSelection
  ]);

  const isAllSelected = selectedTransactionIDs.size === sortedTransactions.length && sortedTransactions.length > 0;
  const isIndeterminate = selectedTransactionIDs.size > 0 && selectedTransactionIDs.size < sortedTransactions.length;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Selection Toolbar */}
      {selectedTransactionIDs.size > 0 && (
        <Paper
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderRadius: 1,
            mb: 1
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {selectedTransactionIDs.size} transaction{selectedTransactionIDs.size !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {displayOptions?.showSubcategory && userProfile?.isCaseContributor() && (
              <Tooltip title="Edit category for selected">
                <Button
                  size="small"
                  startIcon={<CategoryIcon />}
                  onClick={(e) => handleEditSubcategory(e, null, false)}
                  variant="outlined"
                >
                  Edit Category
                </Button>
              </Tooltip>
            )}
            {displayOptions?.showMerchant && userProfile?.isCaseContributor() && (
              <Tooltip title="Edit merchant for selected">
                <Button
                  size="small"
                  startIcon={<PlaceIcon />}
                  onClick={(e) => handleEditMerchant(e, null, false)}
                  variant="outlined"
                >
                  Edit Merchant
                </Button>
              </Tooltip>
            )}
            {displayOptions?.showFlag && userProfile?.isCaseContributor() && (
              <Tooltip title="Edit flag for selected">
                <Button
                  size="small"
                  startIcon={<WarningIcon />}
                  onClick={(e) => handleEditFlag(e, null, false)}
                  variant="outlined"
                >
                  Edit Flag
                </Button>
              </Tooltip>
            )}
            {displayOptions?.showFlag && userProfile?.isCaseContributor() && (
              <Tooltip title="Remove flags from selected">
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={(e) => handleDeleteFlagClicked(e, null, false)}
                  variant="outlined"
                  color="error"
                >
                  Remove Flags
                </Button>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={handleClearSelection}>
            Clear Selection
          </Button>
        </Paper>
      )}

      {/* Summary Section */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          mt: 1
        }}
      >
        {/* Stats row. The sort-levels panel's collapsed summary sits at its
            right; the panel's EXPANDED editor drops below, full bar width. */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Total Transactions
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="60px" height="48px" />
          ) : (
            <Typography variant="h4" fontWeight="bold">
              {formatInteger(sortedTransactions.length)}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Total Amount
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="120px" height="48px" />
          ) : (
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                color: sortedTransactions.reduce((sum, t) => sum + (t.transaction?.amount || 0), 0) < 0
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              }}
            >
              {formatCurrency(sortedTransactions.reduce((sum, t) => sum + (t.transaction?.amount || 0), 0))}
            </Typography>
          )}
        </Box>

        {/* The feedback line stays visible whether the editor is open or shut,
            so the row order is always accounted for — same wording captioned on
            the printed PDF and the Excel Report Info sheet. */}
        <SortLevelsSummary
          sortKeys={sortKeys}
          open={sortPanelOpen}
          onToggleOpen={handleToggleSortPanel}
        />
        </Box>

        {/* Multi-column sorting lives here, NOT on the column headers. */}
        {sortPanelOpen && (
          <SortLevelsEditor
            sortKeys={sortKeys}
            onChange={setSortKeys}
            onReset={handleResetSort}
            visibleFields={sortableVisibleFields}
            isDefault={isDefaultSortKeys(sortKeys)}
          />
        )}
      </Paper>

      {/* Virtual Scrolling Table */}
      <Paper
        sx={{
          mt: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Table Header - Fixed */}
        <Table sx={{ tableLayout: 'fixed', width: '100%', flexShrink: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: '48px' }}>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: '100px' }} aria-sort={ariaSortFor('transactionDate')}>
                <TableSortLabel {...sortLabelProps('transactionDate', 'Date')} sx={{ fontWeight: 'bold' }}>
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '120px' }} aria-sort={ariaSortFor('bankAccountName')}>
                <TableSortLabel {...sortLabelProps('bankAccountName', 'Account')} sx={{ fontWeight: 'bold' }}>
                  Account
                </TableSortLabel>
              </TableCell>
              {displayOptions?.showCardholder && (
                <TableCell sx={{ width: '150px' }} aria-sort={ariaSortFor('cardholderName')}>
                  <TableSortLabel {...sortLabelProps('cardholderName', 'Cardholder')} sx={{ fontWeight: 'bold' }}>
                    Cardholder
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sx={{ width: '300px' }} aria-sort={ariaSortFor('description')}>
                <TableSortLabel {...sortLabelProps('description', 'Description')} sx={{ fontWeight: 'bold' }}>
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '100px' }} aria-sort={ariaSortFor('amount')}>
                <TableSortLabel {...sortLabelProps('amount', 'Amount')} sx={{ fontWeight: 'bold' }}>
                  Amount
                </TableSortLabel>
              </TableCell>
              {displayOptions?.showMerchant && (
                <TableCell sx={{ width: '200px' }} aria-sort={ariaSortFor('merchantName')}>
                  <TableSortLabel {...sortLabelProps('merchantName', 'Merchant')} sx={{ fontWeight: 'bold' }}>
                    Merchant
                  </TableSortLabel>
                </TableCell>
              )}
              {displayOptions?.showSubcategory && (
                <TableCell sx={{ width: '150px' }} aria-sort={ariaSortFor('subcategoryName')}>
                  <TableSortLabel {...sortLabelProps('subcategoryName', 'Subcategory')} sx={{ fontWeight: 'bold' }}>
                    Subcategory
                  </TableSortLabel>
                </TableCell>
              )}
              {displayOptions?.showFlag && (
                <TableCell sx={{ width: '150px' }} aria-sort={ariaSortFor('flagName')}>
                  <TableSortLabel {...sortLabelProps('flagName', 'Flag')} sx={{ fontWeight: 'bold' }}>
                    Flag
                  </TableSortLabel>
                </TableCell>
              )}
              {displayOptions?.showSource && (
                <TableCell sx={{ width: '120px', fontWeight: 'bold' }}>
                  Source
                </TableCell>
              )}
              {displayOptions?.showNotes && (
                <TableCell sx={{ width: '200px', fontWeight: 'bold' }}>
                  Notes
                </TableCell>
              )}
            </TableRow>
          </TableHead>
        </Table>

        {/* Virtual List */}
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton key={index} variant="rectangular" height="73px" sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <List
              rowComponent={VirtualTransactionRow}
              rowCount={sortedTransactions.length}
              rowHeight={getTransactionRowHeight}
              rowProps={{ data: itemData }}
              overscanCount={5}
            >
            </List>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <TransactionDetailDialog
        open={openDetail}
        selectedTransaction={selectedTransaction}
        handleClose={handleClose}
      />

      <CategoryEditDialog
        open={openSubcategoryEdit}
        onClose={handleCloseSubcategoryEdit}
        currentCategoryType={editingSubcategory?.currentCategoryType}
        currentCategory={editingSubcategory?.currentCategory}
        currentSubcategory={editingSubcategory?.currentSubcategory}
        currentRationale={editingSubcategory?.currentRationale}
        currentSegment={editingSubcategory?.currentSegment}
        accountSegment={editingSubcategory?.accountSegment}
        onSave={handleSaveSubcategory}
        isBulkEdit={editingSubcategory?.isBulkEdit}
        selectedCount={editingSubcategory?.selectedCount}
      />

      <MerchantEditDialog
        open={openMerchantEdit}
        onClose={handleCloseMerchantEdit}
        onSave={handleSaveMerchant}
        merchant={editingMerchant}
        isBulkEdit={editingMerchant?.isBulkEdit}
        selectedCount={editingMerchant?.selectedCount}
      />

      <FlagEditDialog
        open={openFlagEdit}
        onClose={handleCloseFlagEdit}
        currentFlag={editingFlag?.currentFlag}
        currentRationale={editingFlag?.currentRationale}
        onSave={handleSaveFlag}
        onDelete={handleDeleteFlag}
        isBulkEdit={editingFlag?.isBulkEdit}
        selectedCount={editingFlag?.selectedCount}
      />

      <SourceEditDialog
        open={openSourceEdit}
        onClose={handleCloseSourceEdit}
        onSaved={mutateTransactions}
        transactionId={editingSource?.transactionId}
        amount={editingSource?.amount}
      />

      <NotesEditDialog
        open={openNotesEdit}
        onClose={handleCloseNotesEdit}
        onSave={handleSaveNotes}
        notes={editingNotes?.notes}
      />

      <DescriptionEditDialog
        open={!!editingDescription}
        onClose={handleCloseDescriptionEdit}
        onSave={handleSaveDescription}
        description={editingDescription?.description}
        originalDescription={editingDescription?.originalDescription}
      />

      {/* Manual transfer review: confirmation dialogs for every change action. */}
      <RejectTransferDialog />
      <ConfirmMatchDialog />
      <LinkMatchDialog />

      {/* Manual transfer review: review the candidate set and re-point the match. */}
      <TransferMatchDialog
        open={Boolean(matchDialogAnchor)}
        onClose={() => setMatchDialogAnchor(null)}
        anchorTransaction={matchDialogAnchor}
        currentPartnerId={matchDialogAnchor?.transfer_partner?.transaction?.id || null}
        currentDisposition={matchDialogAnchor?.transfer_partner?.disposition || null}
        onSelectMatch={handleSelectMatch}
        onConfirm={handleDialogConfirm}
        onReject={handleDialogReject}
      />

      <BulkEditConfirmDialog
        open={bulkEditConfirm.open}
        onClose={() => setBulkEditConfirm({ open: false, type: null, data: null })}
        onConfirm={handleBulkEditConfirm}
        editType={bulkEditConfirm.type}
        editData={bulkEditConfirm.data}
        selectedTransactions={selectedTransactions}
      />

      <Dialog
        open={showDeleteFlagConfirm}
        onClose={handleDeleteFlagCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {editingFlag?.isBulkEdit ? `flags from ${editingFlag.selectedCount} transactions` : 'this flag'}? This action cannot be undone.
          </Typography>
          {editingFlag?.currentFlag && !editingFlag?.isBulkEdit && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Flag: <strong>{editingFlag?.currentFlag}</strong>
              </Typography>
              {editingFlag?.currentRationale && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Rationale: {editingFlag?.currentRationale}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteFlagCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteFlagConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionViewerDisplay;